import React, { useEffect, useState, memo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import moment from "moment";
import { Col, Row } from "reactstrap";
import countries from "../../Country/country.json";
import { Dropdown } from "primereact/dropdown";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

function ForexChart({ formState, setFormState }) {
  const [activeIndex, setActiveIndex] = useState(2);
  const [chartLabels, setChartLabels] = useState([]);
  const [chartData1, setChartData1] = useState([]);
  const [chartData2, setChartData2] = useState([]);

  const { t } = useTranslation();

  const valueTemplate = (option, props) => {
    if (!option || !option.flag) return props.placeholder;
    return (
      <div className="p-dropdown-value">
        <img
          alt={option.name}
          src={option.flag}
          width="20"
          style={{ verticalAlign: "middle", marginRight: "8px" }}
        />
        <span>{t(`${option.currencyCode}.currency.label`)}</span>
      </div>
    );
  };

  const itemTemplate = (option) => {
    return (
      <div className="p-dropdown-item">
        <img
          alt={option.name}
          src={option.flag}
          width="20"
          style={{ verticalAlign: "middle", marginRight: "8px" }}
        />
        <span>{t(`${option.currencyCode}.currency.label`)}</span>
      </div>
    );
  };

  const pair = `${formState.buyCurrency}${formState.sellCurrency}`;

  const chartLinelItem = [
    { label: t("year.label"), val: "year", format: "YYYY" },
    { label: t("month.label"), val: "month", format: "YYYY-MM" },
    { label: t("day.label"), val: "day", format: "YYYY-MM-DD" },
    { label: t("hour.label"), val: "hour", format: "HH:mm" }, // for hourly: HH:mm only
  ];

  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const getTimeRange = (index) => {
    const end = new Date();
    let start = new Date(end);

    switch (index) {
      case 0:
        start.setFullYear(end.getFullYear() - 10);
        break;
      case 1:
        start.setFullYear(end.getFullYear() - 1);
        break;
      case 2:
        start.setDate(end.getDate() - 30);
        break;
      case 3:
        start.setDate(end.getDate() - 3); // get 3 days to ensure full 24h coverage
        break;
      default:
        break;
    }

    return {
      start: formatDate(start),
      end: formatDate(end),
    };
  };

  const fetchData = async () => {
    try {
      const { start, end } = getTimeRange(activeIndex);
      const interval = chartLinelItem[activeIndex].val;

      const response = await fetch(
        "https://aro3jn3404.execute-api.us-east-1.amazonaws.com/default/polygonFetcher",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            pair: pair,
            start: start,
            end: end,
            interval: interval,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const jsonData = await response.json();

      if (jsonData.results) {
        let results = jsonData.results;

        if (activeIndex === 3) {
          const now = Date.now();
          const cutoff = now - 24 * 60 * 60 * 1000;
          results = results.filter((e) => e.t >= cutoff);
        }

        setChartLabels(
          results.map((e) =>
            moment(e.t).format(chartLinelItem[activeIndex].format)
          )
        );
        setChartData1(results.map((e) => e.c));
        setChartData2(results.map((e) => e.c + 0.031));
      } else {
        setChartData1([]);
        setChartData2([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeIndex, pair]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: false },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
        grid: { drawOnChartArea: false },
      },
      y: {
        beginAtZero: false,
      },
    },
  };

  return (
    <div
      style={{
        backgroundColor: "#e9ecef",
        padding: "1rem",
        borderRadius: "8px",
        height: "100%",
        position: "relative",
      }}
    >
      <Row>
        <Col xs={1}></Col>
        <Col xs={4}>
          <div className="form-group">
            <label>{t("im.looking.for.label")}</label>
            <Dropdown
              id="buyCurrency"
              name="buyCurrency"
              value={formState.buyCurrency}
              options={countries}
              onChange={(e) =>
                setFormState({ ...formState, buyCurrency: e.value })
              }
              optionLabel="currencyName"
              optionValue="currencyCode"
              className="w-100"
              filter
              filterBy="currencyName"
              placeholder="Select"
              valueTemplate={valueTemplate}
              itemTemplate={itemTemplate}
            />
          </div>
        </Col>
        <Col xs={2}></Col>
        <Col xs={4}>
          <div className="form-group">
            <label>{t("im.going.to.use.for.trade.label")}</label>
            <Dropdown
              id="sellCurrency"
              name="sellCurrency"
              value={formState.sellCurrency}
              options={countries}
              onChange={(e) =>
                setFormState({ ...formState, sellCurrency: e.value })
              }
              optionLabel="currencyName"
              optionValue="currencyCode"
              className="w-100"
              filter
              filterBy="currencyName"
              placeholder="Select"
              valueTemplate={valueTemplate}
              itemTemplate={itemTemplate}
            />
          </div>
        </Col>
        <Col xs={1}></Col>
      </Row>

      <div className="d-flex gap-2 justify-content-end mb-2">
        {chartLinelItem.map((item, i) => (
          <button
            key={item.label}
            className={`btn btn-sm btn-outline-primary ${
              i === activeIndex ? "active" : ""
            }`}
            onClick={() => setActiveIndex(i)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {chartData1 && chartData1.length > 0 ? (
        <div
          style={{
            backgroundColor: "#e9ecef",
            padding: "1rem",
            borderRadius: "8px",
            height: "70%",
            position: "relative",
          }}
        >
          <Line
            data={{
              labels: chartLabels,
              datasets: [
                {
                  label: "Polygon Rate",
                  data: chartData1,
                  borderColor: "rgb(235, 53, 95)",
                  backgroundColor: "rgba(235, 53, 95, 0.2)",
                  fill: true,
                },
                {
                  label: "Miracle Rate",
                  data: chartData2,
                  borderColor: "rgb(53, 162, 235)",
                  backgroundColor: "rgba(53, 162, 235, 0.2)",
                  fill: true,
                },
              ],
            }}
            options={options}
          />
        </div>
      ) : (
        <p className="text-muted text-center mt-5">No data available.</p>
      )}
    </div>
  );
}

export default memo(ForexChart);
